import type { PricingBreakdown } from '@data/publicPlans';
import { normalizePricingBreakdown } from '@utils/pricingBreakdownUtils';

type CopyFn = (en: string, de: string, hr: string) => string;

type PlanIndicativePricingCardProps = {
	pricing: PricingBreakdown;
	copy: CopyFn;
	/** Show seeker-style disclaimer at bottom */
	showDisclaimer?: boolean;
	/** Hide per-line materials and labor; show min/mid/max only */
	totalOnly?: boolean;
	/** Compact header only (no card wrapper) — for embedding */
	embedded?: boolean;
	className?: string;
};

export default function PlanIndicativePricingCard({
	pricing,
	copy,
	showDisclaimer = true,
	totalOnly = false,
	embedded = false,
	className = ''
}: PlanIndicativePricingCardProps) {
	const normalized = normalizePricingBreakdown(pricing);
	const materials = normalized.materials;
	const labor = normalized.labor;
	const pricingMin = normalized.min;
	const pricingMid = normalized.mid;
	const pricingMax = normalized.max;

	const body = (
		<>
			{!showDisclaimer && (
				<div className="alert alert-light border small mb-3">
					<i className="bi bi-info-circle me-1" aria-hidden />
					{copy(
						'These amounts are indicative and may change in real offers (materials, labor, company pricing, and site conditions).',
						'Diese Beträge sind Richtwerte und können sich in realen Angeboten ändern (Material, Arbeitsleistung, Firmenpreise und Bedingungen vor Ort).',
						'Ovi iznosi su okvirni i mogu se promijeniti u stvarnim ponudama (materijali, rad, cijene tvrtke i uvjeti na lokaciji).'
					)}
				</div>
			)}
			<div className="mb-3">
				<div className="d-flex justify-content-between align-items-center mb-2">
					<span className="text-muted">{copy('Minimum:', 'Minimum:', 'Minimum:')}</span>
					<span className="fw-bold">{pricingMin.toLocaleString()} EUR</span>
				</div>
				<div className="d-flex justify-content-between align-items-center mb-2 py-2 px-2 rounded border border-success bg-success bg-opacity-10">
					<span className="text-muted">{copy('Mid-range:', 'Mittlerer Bereich:', 'Srednji raspon:')}</span>
					<span className="fw-bold text-success fs-5">{pricingMid.toLocaleString()} EUR</span>
				</div>
				<div className="d-flex justify-content-between align-items-center">
					<span className="text-muted">{copy('Maximum:', 'Maximum:', 'Maksimum:')}</span>
					<span className="fw-bold">{pricingMax.toLocaleString()} EUR</span>
				</div>
			</div>
			{!totalOnly && materials.length > 0 && (
				<>
					<hr className="my-3" />
					<div>
						<div className="text-muted mb-2 fw-semibold small">
							<i className="bi bi-box-seam me-1" aria-hidden />
							{copy('Materials:', 'Materialien:', 'Materijali:')}
						</div>
						{materials.map((m, idx) => (
							<div
								key={idx}
								className={`mb-3 pb-3 ${idx < materials.length - 1 ? 'border-bottom border-light' : ''}`}
							>
								<div className="d-flex justify-content-between align-items-start gap-2 mb-1">
									<span className="fw-semibold" style={{ flex: 1, minWidth: 0 }}>
										{m.name}
									</span>
									<span className="fw-bold text-nowrap">{Number(m.total ?? 0).toLocaleString()} EUR</span>
								</div>
								<div className="text-muted ms-1" style={{ fontSize: '0.9em' }}>
									{m.quantity} {m.unit} × {Number(m.unitPrice ?? 0).toLocaleString()} EUR/{m.unit}
								</div>
							</div>
						))}
					</div>
				</>
			)}
			{!totalOnly && labor.length > 0 && (
				<>
					<hr className="my-3" />
					<div>
						<div className="text-muted mb-2 fw-semibold small">
							<i className="bi bi-people me-1" aria-hidden />
							{copy('Labor:', 'Arbeitsleistung:', 'Rad:')}
						</div>
						{labor.map((l, idx) => (
							<div
								key={idx}
								className={`d-flex justify-content-between align-items-center gap-2 mb-2 pb-2 ${
									idx < labor.length - 1 ? 'border-bottom border-light' : ''
								}`}
							>
								<span className="fw-medium">{l.name}</span>
								<span className="fw-bold text-nowrap">{Number(l.total ?? 0).toLocaleString()} EUR</span>
							</div>
						))}
					</div>
				</>
			)}
			{showDisclaimer && (
				<div className="alert alert-info mt-3 mb-0 small border-0" style={{ backgroundColor: '#e7f3ff' }}>
					<i className="bi bi-info-circle me-1" aria-hidden />
					{copy(
						'These are estimates. Actual quotes from gardeners may vary.',
						'Dies sind Schätzwerte. Die tatsächlichen Angebote der Gärtner können abweichen.',
						'Ovo su procjene. Stvarne ponude vrtlara mogu se razlikovati.'
					)}
				</div>
			)}
		</>
	);

	if (embedded) {
		return <div className={className}>{body}</div>;
	}

	return (
		<div className={`card mb-4 ${className}`.trim()}>
			<div className="card-header">
				<h5 className="mb-0">
					<i className="bi bi-currency-euro text-success me-2" aria-hidden />
					{copy('Estimated Pricing', 'Geschätzte Kosten', 'Procijenjena cijena')}
				</h5>
			</div>
			<div className="card-body">{body}</div>
		</div>
	);
}
